/**
 * CanonReviewPane — DM/player triage for proposed canon entries.
 *
 * Smart wrapper: owns the company API client, the three list queries
 * (journal/chronicle/characters), and the five mutations (accept/reject for
 * journal + chronicle, edit for journal + chronicle, reassign for journal).
 * The visual surface lives in ./CanonReviewPaneView — see that file for the
 * triage queue layout, keyboard nav, and accept/edit/reject controls.
 *
 * When `meetingId` is provided the queue is filtered to that session's
 * proposals; otherwise it shows the whole series-wide pending bin.
 */

import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import {
  editChronicleEntry,
  editJournalEntry,
  fetchCharacters,
  fetchChronicleEntries,
  fetchJournalEntries,
  type IJournalEntry,
  reassignJournalEntry,
  setChronicleStatus,
  setJournalStatus,
} from "../../lib/api"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import { CanonReviewPaneView } from "./CanonReviewPaneView"
import type { ICharacterRef, IPendingItem, IProps } from "./interfaces"

function isJournal(item: IPendingItem): item is { kind: "journal"; entry: IJournalEntry } {
  return item.kind === "journal"
}

export function CanonReviewPane({ seriesId, meetingId }: IProps): React.JSX.Element {
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const qc = useQueryClient()

  const journalQuery = useCompanyQuery({
    key: (org) => queryKeys.canonJournal(org, seriesId, "pending"),
    queryFn: (company) => fetchJournalEntries(company, seriesId, { status: "pending" }),
  })

  const chronicleQuery = useCompanyQuery({
    key: (org) => queryKeys.canonChronicle(org, seriesId, "pending"),
    queryFn: (company) => fetchChronicleEntries(company, seriesId, { status: "pending" }),
  })

  const charactersQuery = useCompanyQuery({
    key: (org) => queryKeys.canonCharacters(org, seriesId),
    queryFn: (company) => fetchCharacters(company, seriesId),
  })

  const characters: ICharacterRef[] = useMemo(() => {
    const raw = charactersQuery.data
    if (!Array.isArray(raw)) return []
    return raw.map((c: { id: string; name: string }) => ({ id: c.id, name: c.name }))
  }, [charactersQuery.data])

  const characterNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of characters) m.set(c.id, c.name)
    return m
  }, [characters])

  const items = useMemo<IPendingItem[]>(() => {
    const journals = (journalQuery.data ?? []).filter((e) => !meetingId || e.meetingId === meetingId)
    const chronicle = (chronicleQuery.data ?? []).filter((e) => !meetingId || e.meetingId === meetingId)
    return [
      ...journals.map((entry): IPendingItem => ({ kind: "journal", entry })),
      ...chronicle.map((entry): IPendingItem => ({ kind: "chronicle", entry })),
    ]
  }, [journalQuery.data, chronicleQuery.data, meetingId])

  const journalStatusMutation = useCompanyMutation({
    mutationFn: (company, { id, status }: { id: string; status: "accepted" | "rejected" }) =>
      setJournalStatus(company, seriesId, id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.canonJournal(orgId, seriesId) })
      qc.invalidateQueries({ queryKey: queryKeys.canonChronicle(orgId, seriesId) })
    },
    onError: (err) => notifyError(err),
  })

  const chronicleStatusMutation = useCompanyMutation({
    mutationFn: (company, { id, status }: { id: string; status: "accepted" | "rejected" }) =>
      setChronicleStatus(company, seriesId, id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.canonJournal(orgId, seriesId) })
      qc.invalidateQueries({ queryKey: queryKeys.canonChronicle(orgId, seriesId) })
    },
    onError: (err) => notifyError(err),
  })

  const journalEditMutation = useCompanyMutation({
    mutationFn: (company, { id, patch }: { id: string; patch: { title?: string; body?: string } }) =>
      editJournalEntry(company, seriesId, id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.canonJournal(orgId, seriesId) })
      qc.invalidateQueries({ queryKey: queryKeys.canonChronicle(orgId, seriesId) })
    },
    onError: (err) => notifyError(err),
  })

  const chronicleEditMutation = useCompanyMutation({
    mutationFn: (company, { id, patch }: { id: string; patch: { title?: string; body?: string } }) =>
      editChronicleEntry(company, seriesId, id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.canonJournal(orgId, seriesId) })
      qc.invalidateQueries({ queryKey: queryKeys.canonChronicle(orgId, seriesId) })
    },
    onError: (err) => notifyError(err),
  })

  const reassignMutation = useCompanyMutation({
    mutationFn: (company, { id, characterId }: { id: string; characterId: string }) =>
      reassignJournalEntry(company, seriesId, id, characterId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.canonJournal(orgId, seriesId) })
      qc.invalidateQueries({ queryKey: queryKeys.canonChronicle(orgId, seriesId) })
    },
    onError: (err) => notifyError(err),
  })

  const handleAccept = useCallback(
    (item: IPendingItem) => {
      if (isJournal(item)) {
        journalStatusMutation.mutate({ id: item.entry.id, status: "accepted" })
      } else {
        chronicleStatusMutation.mutate({ id: item.entry.id, status: "accepted" })
      }
    },
    [journalStatusMutation, chronicleStatusMutation],
  )

  const handleReject = useCallback(
    (item: IPendingItem) => {
      if (isJournal(item)) {
        journalStatusMutation.mutate({ id: item.entry.id, status: "rejected" })
      } else {
        chronicleStatusMutation.mutate({ id: item.entry.id, status: "rejected" })
      }
    },
    [journalStatusMutation, chronicleStatusMutation],
  )

  const handleEditSave = useCallback(
    (item: IPendingItem, patch: { title?: string; body?: string }) => {
      if (isJournal(item)) {
        journalEditMutation.mutate({ id: item.entry.id, patch })
      } else {
        chronicleEditMutation.mutate({ id: item.entry.id, patch })
      }
    },
    [journalEditMutation, chronicleEditMutation],
  )

  const handleReassign = useCallback(
    (item: IPendingItem, characterId: string) => {
      if (!isJournal(item)) return
      reassignMutation.mutate({ id: item.entry.id, characterId })
    },
    [reassignMutation],
  )

  return (
    <CanonReviewPaneView
      items={items}
      characters={characters}
      characterNameById={characterNameById}
      loading={journalQuery.isLoading || chronicleQuery.isLoading}
      onAccept={handleAccept}
      onReject={handleReject}
      onEditSave={handleEditSave}
      onReassign={handleReassign}
    />
  )
}
